import React, { useState, useEffect } from 'react';

interface ActivityFeedPreviewProps {
  onClick?: () => void;
}

const ActivityFeedPreview: React.FC<ActivityFeedPreviewProps> = ({ onClick }) => {
  const [visibleItems, setVisibleItems] = useState(0);
  const [newNotification, setNewNotification] = useState(false);

  // Animate items appearing
  useEffect(() => {
    const interval = setInterval(() => {
      setVisibleItems((prev) => (prev < 5 ? prev + 1 : prev));
    }, 400);
    return () => clearInterval(interval);
  }, []);

  // Flash new notification periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setNewNotification(true);
      setTimeout(() => setNewNotification(false), 2000);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const children = [
    { name: 'Emma', avatar: 'E', color: '#ec4899' },
    { name: 'Jack', avatar: 'J', color: '#3b82f6' },
  ];

  const activities = [
    {
      child: 'Emma',
      type: 'achievement',
      message: 'Earned "Math Whiz" badge!',
      time: '2m ago',
      icon: '🏆',
      color: '#f59e0b',
    },
    {
      child: 'Jack',
      type: 'alert',
      message: 'Homework overdue: Fractions',
      time: '15m ago',
      icon: '⚠️',
      color: '#ef4444',
    },
    {
      child: 'Emma',
      type: 'progress',
      message: 'Completed Year 4 English module',
      time: '1h ago',
      icon: '✓',
      color: '#10b981',
    },
    {
      child: 'Jack',
      type: 'streak',
      message: '7-day learning streak!',
      time: '2h ago',
      icon: '🔥',
      color: '#f97316',
    },
    {
      child: 'Emma',
      type: 'struggle',
      message: 'May need help with division',
      time: '3h ago',
      icon: '💡',
      color: '#8b5cf6',
    },
  ];

  const getChildColor = (name: string) => {
    return children.find(c => c.name === name)?.color || '#64748b';
  };

  return (
    <div
      className="relative cursor-pointer transition-all duration-300 hover:scale-[1.02] group"
      onClick={onClick}
      style={{
        background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        overflow: 'hidden',
        width: '320px',
      }}
    >
      {/* Header */}
      <div
        className="px-4 py-2 border-b flex items-center justify-between"
        style={{
          borderColor: 'rgba(255, 255, 255, 0.1)',
          background: 'rgba(236, 72, 153, 0.05)',
        }}
      >
        <span className="text-xs font-semibold" style={{ color: '#94a3b8' }}>
          Family Activity Feed
        </span>
        <div className="flex items-center gap-2">
          {newNotification && (
            <div
              className="px-2 py-0.5 rounded-full text-[9px] font-bold animate-pulse"
              style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#ef4444' }}
            >
              NEW
            </div>
          )}
          <div className="flex -space-x-2">
            {children.map((child) => (
              <div
                key={child.name}
                className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold border-2"
                style={{ background: child.color, borderColor: '#1e293b', color: 'white' }}
              >
                {child.avatar}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Children selector */}
      <div className="px-3 py-2 flex gap-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <div
          className="px-3 py-1 rounded-full text-[10px] font-medium"
          style={{ background: 'rgba(255, 255, 255, 0.1)', color: '#e2e8f0' }}
        >
          All
        </div>
        {children.map((child) => (
          <div
            key={child.name}
            className="px-3 py-1 rounded-full text-[10px] font-medium flex items-center gap-1"
            style={{ background: `${child.color}20`, color: child.color }}
          >
            <div className="w-2 h-2 rounded-full" style={{ background: child.color }} />
            {child.name}
          </div>
        ))}
      </div>

      {/* Activity List */}
      <div className="p-3 space-y-2">
        {activities.slice(0, 5).map((activity, index) => (
          <div
            key={index}
            className="flex items-start gap-2 p-2 rounded-lg transition-all duration-500"
            style={{
              background: activity.type === 'alert' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(255, 255, 255, 0.02)',
              border: activity.type === 'alert' ? '1px solid rgba(239, 68, 68, 0.2)' : '1px solid transparent',
              opacity: index < visibleItems ? 1 : 0,
              transform: index < visibleItems ? 'translateX(0)' : 'translateX(-20px)',
            }}
          >
            {/* Child indicator */}
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0"
              style={{ background: getChildColor(activity.child), color: 'white' }}
            >
              {activity.child.charAt(0)}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1">
                <span className="text-[10px] font-medium" style={{ color: getChildColor(activity.child) }}>
                  {activity.child}
                </span>
                <span className="text-[9px]" style={{ color: '#64748b' }}>
                  • {activity.time}
                </span>
              </div>
              <div className="flex items-center gap-1 mt-0.5">
                <span style={{ fontSize: '11px' }}>{activity.icon}</span>
                <span className="text-[11px] truncate" style={{ color: '#e2e8f0' }}>
                  {activity.message}
                </span>
              </div>
            </div>

            {/* Alert indicator */}
            {activity.type === 'alert' && (
              <div
                className="w-2 h-2 rounded-full animate-pulse shrink-0 mt-2"
                style={{ background: '#ef4444' }}
              />
            )}
          </div>
        ))}
      </div>

      {/* Summary Footer */}
      <div
        className="px-4 py-2 flex items-center justify-between"
        style={{
          background: 'rgba(255, 255, 255, 0.02)',
          borderTop: '1px solid rgba(255, 255, 255, 0.05)',
        }}
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full" style={{ background: '#ef4444' }} />
            <span className="text-[10px]" style={{ color: '#ef4444' }}>
              1 alert
            </span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full" style={{ background: '#10b981' }} />
            <span className="text-[10px]" style={{ color: '#10b981' }}>
              4 updates
            </span>
          </div>
        </div>
        <span className="text-[10px]" style={{ color: '#64748b' }}>
          Today
        </span>
      </div>

      {/* Expand overlay */}
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/20">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{
            background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.9) 0%, rgba(139, 92, 246, 0.9) 100%)',
            boxShadow: '0 8px 32px rgba(236, 72, 153, 0.4)',
          }}
        >
          <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default ActivityFeedPreview;
